import {
  Add,
  CheckBox,
  CheckBoxOutlineBlank,
  Delete,
  Edit,
  Filter as FilterIcon,
  Person,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import ResponsiveTable from "../components/ResponsiveTable";
import AuthContext from "../context/AuthContext";
import { adminService, setorService, usuarioService } from "../services/api";

const Usuarios = () => {
  // ✅ CONFIGURAÇÃO DOS TAMANHOS DOS FILTROS - CSS PURO RESPONSIVO!
  // Valores de 0 a 100 (porcentagem da largura no desktop)
  // Mobile: sempre 100% (empilhados)
  // 💡 TESTE: Mude os valores e veja a diferença imediatamente!
  const TAMANHOS_FILTROS = {
    nome: 25, // 25% - Campo nome
    email: 25, // 25% - Campo email
    status: 15, // 15% - Campo status
    setor: 15, // 15% - Campo setor
    escritorio: 20, // 20% - Campo escritório
  };

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSetores, setLoadingSetores] = useState(false);
  const [loadingEscritorios, setLoadingEscritorios] = useState(false);
  const [error, setError] = useState("");

  // Estado para controlar visibilidade dos filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openSetoresDialog, setOpenSetoresDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState("");

  // Pagination com total
  const [paginacao, setPaginacao] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  // Filters - simplificados para envio ao backend
  const [filters, setFilters] = useState({
    nome: "",
    email: "",
    ativo: "", // vazio = todos
    setor_id: "", // vazio = todos
    escritorio_id_filter: "",
  });

  // Form data
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    senha: "",
    ativo: true,
    role: "padrao",
    setor_ids: [],
    whatsapp: "",
  });

  const { user } = useContext(AuthContext);

  // Estado para lista de escritórios (apenas para super admin)
  const [escritorios, setEscritorios] = useState([]);
  const [setores, setSetores] = useState([]);

  const carregarEscritorios = useCallback(async () => {
    try {
      setLoadingEscritorios(true);
      const response = await adminService.getEscritorios();
      setEscritorios(response || []);
    } catch (err) {
      console.error("Erro ao carregar escritórios:", err);
    } finally {
      setLoadingEscritorios(false);
    }
  }, []);

  const carregarSetores = useCallback(async () => {
    try {
      setLoadingSetores(true);
      const response = await setorService.getAll({ limit: 100 });
      setSetores(response.setores || []);
    } catch (err) {
      console.error("Erro ao carregar setores:", err);
    } finally {
      setLoadingSetores(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_super_admin) {
      carregarEscritorios();
    }
    carregarSetores();
  }, [user, carregarEscritorios, carregarSetores]);

  const carregarUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {
        page: paginacao.page + 1,
        limit: paginacao.limit,
      };

      // Adicionar filtros apenas se preenchidos
      if (filters.nome) params.nome = filters.nome;
      if (filters.email) params.email = filters.email;
      if (filters.ativo) params.ativo = filters.ativo;
      if (filters.setor_id) params.setor_id = filters.setor_id;
      if (filters.escritorio_id_filter) {
        params.escritorio_id_filter = filters.escritorio_id_filter;
      }

      const response = await usuarioService.getAll(params);
      setUsuarios(response.usuarios || []);
      setPaginacao((prev) => ({
        ...prev,
        total: response.pagination?.total || response.total || 0,
      }));
    } catch (err) {
      setError("Erro ao carregar usuários: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [paginacao.page, paginacao.limit, filters]);

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  // Memoizar dados processados para melhor performance
  const processedUsuarios = useMemo(() => {
    return usuarios.map((usuario) => ({
      ...usuario,
      setoresFormatted:
        usuario.setores && usuario.setores.length > 0
          ? usuario.setores.map((setor) => setor.descricao).join(", ")
          : "-",
      whatsappFormatted: usuario.whatsapp
        ? `(${usuario.whatsapp.slice(0, 2)}) ${usuario.whatsapp.slice(2, 7)}-${usuario.whatsapp.slice(7)}`
        : "-",
    }));
  }, [usuarios]);

  const handleOpenDialog = (user = null) => {
    setEditingUser(user);
    setDialogError("");
    setFormData(
      user
        ? {
            nome: user.nome,
            email: user.email,
            senha: "",
            ativo: user.ativo,
            role: user.role || "padrao",
            escritorio_id: user.escritorio_id || "",
            setor_ids: user.setores ? user.setores.map((s) => s.id) : [],
            whatsapp: user.whatsapp || "",
          }
        : {
            nome: "",
            email: "",
            senha: "",
            ativo: true,
            role: "padrao",
            escritorio_id: "",
            setor_ids: [],
            whatsapp: "",
          },
    );
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setDialogError("");
    setFormData({
      nome: "",
      email: "",
      senha: "",
      ativo: true,
      role: "padrao",
      setor_ids: [],
      whatsapp: "",
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setDialogError("");

      if (!formData.nome || !formData.email) {
        setError("Nome e email são obrigatórios");
        return;
      }

      if (!editingUser && !formData.senha) {
        setError("Senha é obrigatória para novos usuários");
        return;
      }

      console.log(
        "formData.whatsapp antes da validação:",
        formData.whatsapp,
        "tipo:",
        typeof formData.whatsapp,
        "length:",
        formData.whatsapp ? formData.whatsapp.length : "null/undefined",
      );

      // Validação do WhatsApp (se preenchido, deve ter 11 dígitos)
      if (formData.whatsapp && formData.whatsapp.length !== 11) {
        setError("WhatsApp deve ter exatamente 11 dígitos (DDD + 9 números)");
        return;
      }

      // Validação adicional para super admin
      if (user?.is_super_admin && !formData.escritorio_id) {
        setError("Escritório é obrigatório para super admins");
        return;
      }

      const userData = {
        nome: formData.nome,
        email: formData.email,
        ativo: formData.ativo,
        role: formData.role,
        setor_ids: formData.setor_ids,
        whatsapp: formData.whatsapp || "",
        ...(user?.is_super_admin && formData.escritorio_id
          ? { escritorio_id: formData.escritorio_id }
          : {}),
      };

      // Incluir senha apenas se for um novo usuário ou se foi fornecida
      if (!editingUser || formData.senha) {
        userData.senha = formData.senha;
      }

      if (editingUser) {
        await usuarioService.update(editingUser.id, userData);
      } else {
        // Super admin usa create, admin usa invite
        if (user?.is_super_admin) {
          await usuarioService.create(userData);
        } else {
          await usuarioService.invite(userData);
        }
      }

      await carregarUsuarios();
      handleCloseDialog();
    } catch (err) {
      // Mostrar erros de salvamento dentro do modal
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (user) => {
    setDeletingUser(user);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setSaving(true);
      setError("");
      await usuarioService.delete(deletingUser.id);
      await carregarUsuarios();
      setOpenConfirmDialog(false);
      setDeletingUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPaginacao((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginacao((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleFilterChange = (field, value) => {
    // Converter valores para o formato esperado pelo backend
    let processedValue = value;
    if (field === "ativo") {
      if (value === "todos") processedValue = "";
      else if (value === "ativo") processedValue = "true";
      else if (value === "inativo") processedValue = "false";
    }
    if (field === "setor_id" && value === "todos") {
      processedValue = "";
    }

    setFilters({ ...filters, [field]: processedValue });
    setPaginacao((prev) => ({ ...prev, page: 0 })); // Reset to first page when filtering
  };

  const handleFormChange = (field, value) => {
    // Validação específica para WhatsApp
    if (field === "whatsapp") {
      // Remove caracteres não numéricos e limita a 11 dígitos
      const numericValue = value.replace(/\D/g, "").slice(0, 11);
      setFormData({ ...formData, [field]: numericValue });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSetorToggle = (setorId) => {
    const currentSetores = formData.setor_ids || [];
    const newSetores = currentSetores.includes(setorId)
      ? currentSetores.filter((id) => id !== setorId)
      : [...currentSetores, setorId];

    setFormData({ ...formData, setor_ids: newSetores });
  };

  const handleOpenSetoresDialog = () => {
    setOpenSetoresDialog(true);
  };

  const handleCloseSetoresDialog = () => {
    setOpenSetoresDialog(false);
  };

  // Removido o retorno com spinner global para não desmontar os filtros durante a digitação

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
          Usuários
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            sx={{ mr: 2 }}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: "#1976d2" }}
          >
            Novo usuário
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      {mostrarFiltros && (
        <Card
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, color: "#333", fontWeight: 600 }}
          >
            Filtros
          </Typography>

          {/* ✅ SOLUÇÃO CSS PURA - Funciona em qualquer versão do Material-UI */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexWrap: { xs: "wrap", md: "nowrap" }, // Mobile: quebra linha, Desktop: mesma linha
              alignItems: "center",
            }}
          >
            {/* Nome */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.nome}%`,
                minWidth: { xs: "100%", md: "200px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.nome}%` },
              }}
            >
              <TextField
                fullWidth
                label="Nome"
                placeholder="Buscar por nome..."
                value={filters.nome}
                onChange={(e) => handleFilterChange("nome", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
              />
            </Box>

            {/* Email */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.email}%`,
                minWidth: { xs: "100%", md: "200px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.email}%` },
              }}
            >
              <TextField
                fullWidth
                label="Email"
                placeholder="Buscar por email..."
                value={filters.email}
                onChange={(e) => handleFilterChange("email", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
              />
            </Box>

            {/* Status */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.status}%`,
                minWidth: { xs: "100%", md: "120px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.status}%` },
              }}
            >
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.ativo}
                  label="Status"
                  onChange={(e) => handleFilterChange("ativo", e.target.value)}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="inativo">Inativo</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Setor */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.setor}%`,
                minWidth: { xs: "100%", md: "150px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.setor}%` },
              }}
            >
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={filters.setor_id}
                  label="Setor"
                  onChange={(e) =>
                    handleFilterChange("setor_id", e.target.value)
                  }
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  {loadingSetores ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Carregando...
                    </MenuItem>
                  ) : (
                    setores.map((setor) => (
                      <MenuItem key={setor.id} value={setor.id.toString()}>
                        {setor.descricao}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Escritório - apenas para super admin */}
            {user?.is_super_admin && (
              <Box
                sx={{
                  flex: `0 1 ${TAMANHOS_FILTROS.escritorio}%`,
                  minWidth: { xs: "100%", md: "150px" },
                  maxWidth: { md: `${TAMANHOS_FILTROS.escritorio}%` },
                }}
              >
                <FormControl fullWidth>
                  <InputLabel>Escritório</InputLabel>
                  <Select
                    value={filters.escritorio_id_filter}
                    label="Escritório"
                    onChange={(e) =>
                      handleFilterChange("escritorio_id_filter", e.target.value)
                    }
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    <MenuItem value="">Todos os escritórios</MenuItem>
                    {loadingEscritorios ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Carregando...
                      </MenuItem>
                    ) : (
                      escritorios.map((escritorio) => (
                        <MenuItem key={escritorio.id} value={escritorio.id}>
                          {escritorio.nome}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Botão para limpar filtros */}
          <Box sx={{ mt: 3 }}>
            <Button
              onClick={() => {
                setFilters({
                  nome: "",
                  email: "",
                  ativo: "",
                  setor_id: "",
                  escritorio_id_filter: "",
                });
                setPaginacao((prev) => ({ ...prev, page: 0 }));
              }}
              variant="outlined"
              size="small"
            >
              Limpar filtros
            </Button>
          </Box>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <ResponsiveTable
          data={processedUsuarios}
          loading={loading}
          emptyMessage="Nenhum usuário encontrado. Cadastre primeiro os setores, pois os usuários serão associados a eles."
          columns={[
            {
              field: "nome",
              label: "Nome",
              minWidth: 200,
              format: (value, item) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Person sx={{ color: "text.secondary" }} />
                  {value}
                </Box>
              ),
            },
            { field: "email", label: "Email", minWidth: 200 },
            {
              field: "whatsappFormatted",
              label: "WhatsApp",
              minWidth: 150,
            },
            {
              field: "role",
              label: "Tipo",
              minWidth: 120,
              format: (value) =>
                value === "admin" ? "Administrador" : "Padrão",
            },
            {
              field: "ativo",
              label: "Status",
              chip: true,
              format: (value) => (value ? "Ativo" : "Inativo"),
              chipColor: (value) => (value ? "success" : "error"),
              minWidth: 100,
            },
            {
              field: "setoresFormatted",
              label: "Setores",
              minWidth: 200,
            },
            ...(user?.is_super_admin
              ? [
                  {
                    field: "escritorio",
                    label: "Escritório",
                    minWidth: 100,
                    format: (value) => value?.nome || "-",
                  },
                ]
              : []),
          ]}
          actions={[
            {
              icon: <Edit />,
              label: "Editar",
              color: "info",
              onClick: (usuario) => handleOpenDialog(usuario),
            },
            {
              icon: <Delete />,
              label: "Excluir",
              color: "error",
              onClick: (usuario) => handleDelete(usuario),
              // disabled: (usuario) => usuario.ativo,
            },
          ]}
        />
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={paginacao.total}
          rowsPerPage={paginacao.limit}
          page={paginacao.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Card>

      {/* Dialog para criar/editar usuário */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? "Editar usuário" : "Novo usuário"}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Nome"
              value={formData.nome}
              onChange={(e) => handleFormChange("nome", e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="WhatsApp"
              placeholder="11999999999"
              value={formData.whatsapp}
              onChange={(e) => handleFormChange("whatsapp", e.target.value)}
              inputProps={{
                maxLength: 11,
                pattern: "[0-9]{11}",
              }}
              helperText="Digite apenas números (DDD + 9 dígitos)"
            />
            <TextField
              fullWidth
              label={
                editingUser ? "Nova senha (deixe vazio para manter)" : "Senha"
              }
              type="password"
              value={formData.senha}
              onChange={(e) => handleFormChange("senha", e.target.value)}
              required={!editingUser}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.role || "padrao"}
                label="Tipo"
                onChange={(e) => handleFormChange("role", e.target.value)}
              >
                <MenuItem value="admin">Administrador</MenuItem>
                <MenuItem value="padrao">Padrão</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.ativo}
                label="Status"
                onChange={(e) => handleFormChange("ativo", e.target.value)}
              >
                <MenuItem value={true}>Ativo</MenuItem>
                <MenuItem value={false}>Inativo</MenuItem>
              </Select>
            </FormControl>
            {!loadingSetores && setores.length === 0 && (
              <Alert severity="error">
                Recomendamos criar os setores primeiro para poder atribuir
                setores ao novo usuário.
              </Alert>
            )}
            <FormControl fullWidth>
              <TextField
                fullWidth
                label="Setores"
                value={
                  formData.setor_ids?.length === 0
                    ? "Sem setores"
                    : formData.setor_ids?.length === 1
                      ? setores.find((s) => s.id === formData.setor_ids[0])
                          ?.descricao || "Setor não encontrado"
                      : `${formData.setor_ids?.length || 0} setores selecionados`
                }
                onClick={handleOpenSetoresDialog}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenSetoresDialog}
                        sx={{ minWidth: "auto", px: 1 }}
                      >
                        {formData.setor_ids?.length > 0
                          ? "Alterar"
                          : "Selecionar"}
                      </Button>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    cursor: "pointer",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2",
                      },
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: "0.75rem",
                }}
              >
                💡 Clique para selecionar os setores deste usuário
              </Typography>
            </FormControl>
            {user?.is_super_admin && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Escritório</InputLabel>
                <Select
                  value={formData.escritorio_id || ""}
                  label="Escritório"
                  onChange={(e) =>
                    handleFormChange("escritorio_id", e.target.value)
                  }
                >
                  <MenuItem value="">Selecione um escritório</MenuItem>
                  {escritorios.map((esc) => (
                    <MenuItem key={esc.id} value={esc.id}>
                      {esc.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação para excluir */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir permanentemente o usuário "
            {deletingUser?.nome}"? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para seleção de setores */}
      <Dialog
        open={openSetoresDialog}
        onClose={handleCloseSetoresDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Selecionar setores
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Escolha os setores aos quais este usuário pertence
          </Typography>
        </DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {setores.map((setor, index) => (
              <React.Fragment key={setor.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleSetorToggle(setor.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        checked={
                          formData.setor_ids?.includes(setor.id) || false
                        }
                        icon={<CheckBoxOutlineBlank />}
                        checkedIcon={<CheckBox />}
                        sx={{
                          color: "primary.main",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={setor.descricao}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontWeight: formData.setor_ids?.includes(setor.id)
                            ? 600
                            : 400,
                          color: formData.setor_ids?.includes(setor.id)
                            ? "primary.main"
                            : "text.primary",
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {index < setores.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
          {setores.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                Nenhum setor disponível
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSetoresDialog}>Salvar</Button>
          <Button
            onClick={() => {
              setFormData({ ...formData, setor_ids: [] });
            }}
            variant="outlined"
            color="secondary"
          >
            Limpar seleção
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Usuarios;
